import React, { useCallback, useMemo } from 'react'
import { Box, FormHelperText, Icon, InputAdornment, TextFieldProps } from '@mui/material'
import TextFieldNN from './TextFieldNN'

type StatePassword = {
  value: string
  isValid: boolean
}

type Props = {
  onChange: (state: StatePassword) => void
  label: string
  onKeyDownEnter?: () => void
  validate?: boolean
  focusOnLoad?: boolean
  autoComplete?: TextFieldProps['autoComplete']
}

const MIN_LENGTH = 6

const PasswordInput = (props: Props) => {
  const { onKeyDownEnter, label, validate = true } = props

  const [password, setPassword] = React.useState('')
  const [isVisible, setIsVisible] = React.useState(false)
  const [touched, setTouched] = React.useState(false)

  const getErrors = useCallback((password: string): string[] => {
    const errors: string[] = []

    const isLongEnough = password.length >= MIN_LENGTH
    if (!isLongEnough) errors.push(`Must be at least ${MIN_LENGTH} characters long`)

    const hasLowerCase = password.toUpperCase() !== password
    if (!hasLowerCase) errors.push('Must have at least one lowercase letter')

    const hasUpperCase = password.toLowerCase() !== password
    if (!hasUpperCase) errors.push('Must have at least one uppercase letter')

    const hasNumber = /\d/.test(password)
    if (!hasNumber) errors.push('Must have at least one number')

    return errors
  }, [])

  const isValid = useMemo(() => {
    const errors = getErrors(password)
    return errors.length === 0
  }, [getErrors, password])

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const isValid = getErrors(value).length === 0
    setPassword(value)
    props.onChange({ value, isValid })
  }

  const showError = validate && touched && !isValid

  return (
    <Box>
      <TextFieldNN
        id="password"
        size="small"
        label={label}
        value={password}
        onBlur={() => setTouched(true)}
        onFocus={() => setTouched(true)}
        onChange={onChange}
        autoComplete={props.autoComplete}
        type={isVisible ? 'text' : 'password'}
        endAdornment={
          <InputAdornment position="end">
            <Icon className="cursor-pointer" onClick={() => setIsVisible((v) => !v)}>
              {isVisible ? 'visibility_off' : 'visibility'}
            </Icon>
          </InputAdornment>
        }
        onKeyDown={({ key }) => {
          if (key === 'Enter') onKeyDownEnter?.()
        }}
        error={showError}
        inputRef={props.focusOnLoad ? (input) => input?.focus() : undefined}
        sx={{ ...(!validate && { minHeight: '80px' }) }}
      />
      {showError && (
        <FormHelperText
          sx={{ mt: '-12px' }}
          className="text-red"
          dangerouslySetInnerHTML={{
            __html: getErrors(password)
              .map((e) => '- ' + e)
              .join('<br />'),
          }}
        />
      )}
    </Box>
  )
}
export default PasswordInput
